import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Grid3x3, Hexagon, Image, LayoutGrid, Lightbulb, Share2, Spline } from "lucide-react";
import { CinematicVideo } from "@/components/CinematicVideo";

const GAME_TEASERS = [
  { id: "takuzu", icon: Grid3x3 },
  { id: "rikudo", icon: Hexagon },
  { id: "nonogram", icon: Image },
  { id: "hitori", icon: LayoutGrid },
  { id: "akari", icon: Lightbulb },
  { id: "hashi", icon: Share2 },
  { id: "masyu", icon: Spline },
];

const VIDEO_INTRO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/6jvl6sxj_gemini_generated_video_b1ba1056.mp4";
const VIDEO_OUTRO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/b0o5j5ta_gemini_generated_video_4b3b18de.mp4";
const POSTER_GAMES = "https://static.prod-images.emergentagent.com/jobs/08c324fb-59b2-4fff-a725-0bafcdd9ead9/images/634a20c1dc8ad3998a84b7a94e564ad0107ade8c222b5da57221c83dbff0a547.png";

export function JeuxLogique() {
  const { t } = useTranslation();

  return (
    <section className="max-w-6xl mx-auto px-6 py-14 sm:py-24" data-testid="jeux-logique-section">
      <div className="text-center mb-10">
        <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-5">{t("games.landingEyebrow")}</p>
        <h2 className="font-display text-gradient-orange text-3xl sm:text-4xl italic mx-auto max-w-2xl leading-tight">
          {t("games.landingTitle")}
        </h2>
        <p className="mt-6 text-foreground/60 leading-relaxed max-w-2xl mx-auto">{t("games.landingSubtitle")}</p>
      </div>

      <div className="max-w-3xl mx-auto mb-14">
        <CinematicVideo fade noSound silver src={VIDEO_INTRO} testId="jeux-cinematic-video" caption={t("games.landingSubtitle")} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
        {GAME_TEASERS.map(({ id, icon: Icon }, i) => (
          <motion.div
            key={id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
            style={{ animationDelay: `${(i % 4) * 0.6}s` }}
            className="glass frame-pulse rounded-2xl p-6 flex items-start gap-4"
            data-testid={`jeux-teaser-${i}`}
          >
            <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon className="text-primary" size={20} strokeWidth={1.6} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <h3 className="font-display text-xl">{t(`games.${id}Name`)}</h3>
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/15 text-primary">{t("games.premiumBadge")}</span>
              </div>
              <p className="text-foreground/55 leading-relaxed text-sm">{t(`games.${id}Tagline`)}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="max-w-3xl mx-auto mb-14">
        <CinematicVideo noSound silver src={VIDEO_OUTRO} testId="jeux-cinematic-video-2" poster={POSTER_GAMES} caption={`${t("games.merit1Title")}. ${t("games.merit2Title")}. ${t("games.merit3Title")}.`} />
      </div>

      <div className="flex justify-center">
        <Link
          to="/entrer"
          data-testid="jeux-landing-cta"
          className="btn-frost-orange inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm"
        >
          {t("games.landingCta")}
        </Link>
      </div>
    </section>
  );
}
